import React, {useEffect, useState} from 'react';
import './careers.css';
import click from '../Images/click.jpg';
import { DATA_UPDATED_EVENT, getJobs, saveApplicationToGoogleSheet } from '../dataStore';

const initialForm = {
  name: '',
  phone: '',
  email: '',
  role: '',
  years: '',
  ctc: '',
  notice: '',
  location: '',
};

const MAX_RESUME_SIZE = 5 * 1024 * 1024;

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = () => reject(new Error('The resume could not be read.'));
  reader.readAsDataURL(file);
});

export default function Careers(){
  const [isOpen,setIsOpen]=useState(false);
  const [form,setForm]=useState(initialForm);
  const [resume,setResume]=useState(null);
  const [status,setStatus]=useState('');
  const [error,setError]=useState('');
  const [submitting,setSubmitting]=useState(false);
  const [jobs,setJobs]=useState(getJobs);
  const filtered = jobs;

  useEffect(() => {
    const refreshJobs = () => setJobs(getJobs());
    window.addEventListener(DATA_UPDATED_EVENT, refreshJobs);
    window.addEventListener('storage', refreshJobs);
    return () => {
      window.removeEventListener(DATA_UPDATED_EVENT, refreshJobs);
      window.removeEventListener('storage', refreshJobs);
    };
  }, []);

  const openModal = (job) => {
    setForm({...initialForm, role: job ? job.title : ''});
    setResume(null);
    setStatus('');
    setError('');
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setForm(initialForm);
    setResume(null);
  };

  const handleChange = (e) => {
    const {name,value} = e.target;
    setForm(prev => ({...prev,[name]:value}));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError('');
    if (file && file.size > MAX_RESUME_SIZE) {
      e.target.value = '';
      setResume(null);
      setError('Resume must be 5 MB or smaller.');
      return;
    }
    setResume(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      setError('Please upload your resume.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const resumeData = await readFileAsBase64(resume);
      await saveApplicationToGoogleSheet({
        ...form,
        resume: {
          name: resume.name,
          mimeType: resume.type || 'application/octet-stream',
          data: resumeData,
        },
      });
      setStatus('Your application has been submitted successfully.');
      closeModal();
    } catch (submissionError) {
      console.error('Error submitting application:', submissionError);
      setError('Your application could not be saved. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="careers" className="zm-careers">
      <div className="zm-container zm-careers-inner">
        <div className="careers-head">
          <h2>Your next opportunity starts here</h2>
          <p>Whether you're an experienced technology professional or just beginning your career, ZeitaMax connects you with leading organizations that value innovation and talent.</p>
        </div>
        <div className="jobs-wrap">
          <div className="jobs-search">
            {/* <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search roles, e.g. engineer" /> */}
            <button type="button" className="careers-apply-cta" onClick={() => openModal()}>
              <span>Apply Now</span>
              <span className="careers-apply-arrow" aria-hidden="true">→</span>
            </button>
          </div>

          <div className="careers-illustration-wrap">
            <img src={click} alt="Careers" className="careers-illustration" />
          </div>
          {status && <p className="form-status">{status}</p>}
          <div className="jobs-list" id="jobs">
            {filtered.map(job => (
              <article className="job" key={job.id}>
                <div className="job-content">
                  <h3>{job.title}</h3>
                  <div className="job-meta"><span>{job.location}</span><span>{job.type}</span></div>
                  <p>{job.description}</p>
                </div>
                <button type="button" className="btn primary" onClick={() => openModal(job)}>Apply now</button>
              </article>
            ))}
            <div className="single-apply-wrap">
              {/* <button type="button" className="btn ghost" onClick={() => openModal()}>Submit a general application</button> */}
            </div>
            {/* {filtered.length===0 && <div className="no">No current openings match your search. You can still submit a general application.</div>} */}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="application-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Apply for this role</h3>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form className="application-form" onSubmit={handleSubmit}>
              <div className="form-grid">
                <label>
                  <span>Full Name</span>
                  <input name="name" value={form.name} onChange={handleChange} required />
                </label>
                <label>
                  <span>Phone Number</span>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} required />
                </label>
                <label>
                  <span>Email</span>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required />
                </label>
                <label>
                  <span>Role</span>
                  <input name="role" value={form.role} onChange={handleChange} required />
                </label>
                <label>
                  <span>Years of Experience</span>
                  <input name="years" value={form.years} onChange={handleChange} required />
                </label>
                <label>
                  <span>CTC</span>
                  <input name="ctc" value={form.ctc} onChange={handleChange} required />
                </label>
                <label>
                  <span>Notice Period</span>
                  <input name="notice" value={form.notice} onChange={handleChange} required />
                </label>
                <label>
                  <span>Location</span>
                  <input name="location" value={form.location} onChange={handleChange} required />
                </label>
                <label className="file-field">
                  <span>Upload Resume</span>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} required />
                  <small>PDF, DOC or DOCX; maximum 5 MB.</small>
                </label>
              </div>

              {error && <p className="application-error" role="alert">{error}</p>}

              <div className="modal-actions">
                <button type="button" className="btn ghost" onClick={closeModal} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}
